import { CheckCircle, Download, ExternalLink, Filter, Search, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import { API_URL, apiFetch } from '../config/api'

interface Skill {
  id: number
  skill_name: string
  skill_url: string
  category: string
  description: string | null
  verified: boolean
  install_count: number
  created_at: string
  installed_at?: string
}

interface Category {
  category: string
  count: number
}

export default function SkillsMarketplace() {
  const [skills, setSkills] = useState<Skill[]>([])
  const [installedSkills, setInstalledSkills] = useState<Skill[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [verifiedOnly, setVerifiedOnly] = useState(false)
  const [activeTab, setActiveTab] = useState<'marketplace' | 'installed'>('marketplace')

  useEffect(() => {
    void fetchSkills()
    void fetchCategories()
    void fetchInstalledSkills()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, verifiedOnly])

  const fetchSkills = async () => {
    try {
      const params = new URLSearchParams()
      if (selectedCategory) {
        params.append('category', selectedCategory)
      }
      if (verifiedOnly) {
        params.append('verified_only', 'true')
      }

      const res = await apiFetch(`${API_URL}/api/skills?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (res.ok) {
        const data = await res.json() as Skill[]
        setSkills(data)
      }
    } catch (error) {
      console.error('Failed to fetch skills:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/skills/categories`)
      if (res.ok) {
        const data = await res.json() as { categories: Category[] }
        setCategories(data.categories)
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchInstalledSkills = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/skills/installed/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (res.ok) {
        const data = await res.json() as { skills: Skill[] }
        setInstalledSkills(data.skills)
      }
    } catch (error) {
      console.error('Failed to fetch installed skills:', error)
    }
  }

  const handleInstall = async (skillId: number) => {
    try {
      const res = await apiFetch(`${API_URL}/api/skills/install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ skill_id: skillId })
      })

      if (res.ok) {
        const data = await res.json() as { success: boolean; message: string }
        // eslint-disable-next-line no-alert
        alert(data.message)
        void fetchSkills()
        void fetchInstalledSkills()
      }
    } catch (error) {
      console.error('Failed to install skill:', error)
    }
  }

  const handleUninstall = async (skillId: number) => {
    // eslint-disable-next-line no-alert
    if (!confirm('Are you sure you want to uninstall this skill?')) {
      return
    }

    try {
      const res = await apiFetch(`${API_URL}/api/skills/uninstall/${skillId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (res.ok) {
        // eslint-disable-next-line no-alert
        alert('Skill uninstalled successfully')
        void fetchSkills()
        void fetchInstalledSkills()
      }
    } catch (error) {
      console.error('Failed to uninstall skill:', error)
    }
  }

  const filteredSkills = skills.filter(skill =>
    skill.skill_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (skill.description && skill.description.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const isInstalled = (skillId: number) => {
    return installedSkills.some(s => s.id === skillId)
  }

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px' }}>
            Skills Marketplace
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Browse and install OpenClaw skills for your agents
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid var(--border-color)' }}>
          <button
            onClick={() => setActiveTab('marketplace')}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'marketplace' ? '2px solid var(--primary-color)' : 'none',
              color: activeTab === 'marketplace' ? 'var(--primary-color)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: activeTab === 'marketplace' ? 'bold' : 'normal'
            }}
          >
            Marketplace ({skills.length})
          </button>
          <button
            onClick={() => setActiveTab('installed')}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'installed' ? '2px solid var(--primary-color)' : 'none',
              color: activeTab === 'installed' ? 'var(--primary-color)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: activeTab === 'installed' ? 'bold' : 'normal'
            }}
          >
            Installed ({installedSkills.length})
          </button>
        </div>

        {activeTab === 'marketplace' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {/* Search */}
              <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
                <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 10px 10px 40px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--card-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              {/* Category Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Filter size={20} style={{ color: 'var(--text-secondary)' }} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--card-bg)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.category} value={cat.category}>
                      {cat.category} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Verified Only */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={(e) => setVerifiedOnly(e.target.checked)}
                />
                <span>Verified only</span>
              </label>
            </div>

            {/* Skills Grid */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading skills...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {filteredSkills.map(skill => (
                  <div
                    key={skill.id}
                    style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                          {skill.skill_name}
                        </h3>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          background: 'var(--primary-color)',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {skill.category}
                        </span>
                      </div>
                      {skill.verified && (
                        <CheckCircle size={20} style={{ color: 'var(--success-color)' }} />
                      )}
                    </div>

                    {/* Description */}
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', flex: 1 }}>
                      {skill.description || 'No description available'}
                    </p>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      <span>
                        <Download size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        {skill.install_count} installs
                      </span>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => void handleInstall(skill.id)}
                        disabled={isInstalled(skill.id)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: isInstalled(skill.id) ? 'var(--border-color)' : 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: isInstalled(skill.id) ? 'not-allowed' : 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        {isInstalled(skill.id) ? 'Installed' : 'Install'}
                      </button>
                      <a
                        href={skill.skill_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '10px',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-primary)',
                          textDecoration: 'none'
                        }}
                      >
                        <ExternalLink size={20} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'installed' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {installedSkills.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', gridColumn: '1 / -1' }}>
                No skills installed yet
              </div>
            ) : (
              installedSkills.map(skill => (
                <div
                  key={skill.id}
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                        {skill.skill_name}
                      </h3>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'var(--primary-color)',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {skill.category}
                      </span>
                    </div>
                    {skill.verified && (
                      <CheckCircle size={20} style={{ color: 'var(--success-color)' }} />
                    )}
                  </div>

                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', flex: 1 }}>
                    {skill.description || 'No description available'}
                  </p>

                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Installed: {skill.installed_at ? new Date(skill.installed_at).toLocaleDateString() : 'Unknown'}
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => void handleUninstall(skill.id)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'var(--danger-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Trash2 size={16} />
                      Uninstall
                    </button>
                    <a
                      href={skill.skill_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '10px',
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-primary)',
                        textDecoration: 'none'
                      }}
                    >
                      <ExternalLink size={20} />
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </MainLayout>
  )
}
